import React, { useEffect, useState } from "react";
import { Receipt } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";

function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default function PaymentHistory({ clientId, refreshKey }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!clientId) return;
      setLoading(true);
      try {
        const res = await api.get(`/clients/${clientId}/payments`);
        if (!cancelled) setPayments(res.data);
      } catch (err) {
        // silent — handled upstream
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [clientId, refreshKey]);

  return (
    <Card className="rounded-2xl shadow-sm">
      <CardContent className="p-6">
        <h3 className="mb-4 flex items-center gap-2 text-xl font-bold">
          <Receipt size={20} /> Payment History
        </h3>
        {loading && <p className="text-slate-500">Loading...</p>}
        {!loading && payments.length === 0 && (
          <p className="text-slate-500">No payments recorded yet.</p>
        )}
        {!loading && payments.length > 0 && (
          <div className="overflow-x-auto" data-testid="payment-history">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3 text-right">Balance Before</th>
                  <th className="py-2 pr-3 text-right">Balance After</th>
                  <th className="py-2 pr-3 text-right">Total Paid</th>
                  <th className="py-2 pr-3">Note</th>
                  <th className="py-2">Recorded By</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-medium">{p.date}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-green-700">
                      +{money(p.amount)}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-600">{money(p.previousBalance)}</td>
                    <td className="py-2 pr-3 text-right">{money(p.newBalance)}</td>
                    <td className="py-2 pr-3 text-right">{money(p.newTotalPaid)}</td>
                    <td className="py-2 pr-3 text-slate-700">{p.note || "—"}</td>
                    <td className="py-2 text-slate-500">{p.recordedBy || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
